import { templateType, type default as TemplateType } from "./TemplateType";
import { projectsId, type ProjectsId } from "./Projects";
import { templateStatus, type default as TemplateStatus } from "./TemplateStatus";
import { localeEnum, type default as LocaleEnum } from "./LocaleEnum";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for templates */
export type TemplatesId = string & { __flavor: "TemplatesId" };

/**
 * Represents the table public.templates
 * @uniqueConstraints {"templates_pkey":{"name":"templates_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"template_project_slug_unique":{"name":"template_project_slug_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"project_id","definition":"project_id","predicate":null},{"name":"slug","definition":"slug","predicate":null}]},"template_project_name_unique":{"name":"template_project_name_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"project_id","definition":"project_id","predicate":null},{"name":"name","definition":"name","predicate":null}]}}
 */
export default interface TemplatesTable {
  id: ColumnType<TemplatesId, TemplatesId | undefined, TemplatesId>;

  name: ColumnType<string, string, string>;

  type: ColumnType<TemplateType, TemplateType, TemplateType>;

  description: ColumnType<string | null, string | null, string | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>;

  projectId: ColumnType<ProjectsId, ProjectsId, ProjectsId>;

  slug: ColumnType<string, string, string>;

  status: ColumnType<TemplateStatus, TemplateStatus | undefined, TemplateStatus>;

  locales: ColumnType<LocaleEnum[], LocaleEnum[], LocaleEnum[]>;
}

export type Templates = Selectable<TemplatesTable>;

export type NewTemplates = Insertable<TemplatesTable>;

export type TemplatesUpdate = Updateable<TemplatesTable>;

export const templatesId = z.string() as unknown as z.Schema<TemplatesId>;

export const templates = z.object({
  id: templatesId,
  name: z.string(),
  type: templateType,
  description: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  projectId: projectsId,
  slug: z.string(),
  status: templateStatus,
  locales: localeEnum,
});

export const templatesInitializer = z.object({
  id: templatesId.optional(),
  name: z.string(),
  type: templateType,
  description: z.string().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  projectId: projectsId,
  slug: z.string(),
  status: templateStatus.optional(),
  locales: localeEnum,
});

export const templatesMutator = z.object({
  id: templatesId.optional(),
  name: z.string().optional(),
  type: templateType.optional(),
  description: z.string().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  projectId: projectsId.optional(),
  slug: z.string().optional(),
  status: templateStatus.optional(),
  locales: localeEnum.optional(),
});

// ── constraints for Templates ─────────────────────────────────
export const TemplatesConstraints = {
  templates_pkey: {
    name: "templates_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  template_project_slug_unique: {
    name: "template_project_slug_unique",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "project_id", definition: "project_id", predicate: null },
      { name: "slug", definition: "slug", predicate: null },
    ],
  },
  template_project_name_unique: {
    name: "template_project_name_unique",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "project_id", definition: "project_id", predicate: null },
      { name: "name", definition: "name", predicate: null },
    ],
  },
} as const;
export type TemplatesConstraintsType = typeof TemplatesConstraints;
